import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "../utils/axiosInstance";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Spinner,
  InputGroup,
  Badge,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import { getApiBase } from "../utils/useApiBase";
const API_URL = `${getApiBase()}/isuku`;

const IsukuIsukura = () => {
  // ═══════════════════════════════════════════
  // STATES
  // ═══════════════════════════════════════════
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [alertInfo, setAlertInfo] = useState({ show: false, variant: "", message: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [visibleCount, setVisibleCount] = useState(40);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 768
  );

  const [formData, setFormData] = useState({
    amazina_y_umuturage: "",
    citizen_id: "",
    ufite_ubwiherero: "Yego",
    ufite_ahobojyera: "Yego",
    ufite_ubukarabiro_bwo_kurituwarete: "Yego",
    ufite_ingarani: "Yego",
    inzu_ikoze_amasuku: "Yego",
  });

  // Household heads registered under Isibo, for the auto-populate
  // dropdown instead of hand-typing a name.
  const [householdHeads, setHouseholdHeads] = useState([]);
  useEffect(() => {
    axios.get(`${getApiBase()}/isibo/household-heads`)
      .then((res) => setHouseholdHeads(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHouseholdHeads([]));
  }, []);

  const observerRef = useRef(null);
  const scrollRef = useRef(null);

  const token = localStorage.getItem("token");
  const config = { headers: { Authorization: `Bearer ${token}` } };

  // ═══════════════════════════════════════════
  // DEVICE DETECTION
  // ═══════════════════════════════════════════
  useEffect(() => {
    const onResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const dev = useMemo(() => {
    const w = windowWidth;
    const h = windowHeight;
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
    const isAndroid = /Android/.test(ua);

    return {
      isGalaxyFold: w <= 300,
      isMiniPhone: w > 300 && w <= 320,
      isSmallPhone: w > 320 && w <= 375,
      isMedPhone: w > 375 && w <= 414,
      isLargePhone: w > 414 && w < 576,
      isSmallTablet: w >= 576 && w < 768,
      isTablet: w >= 768 && w < 992,
      isLargeTablet: w >= 992 && w < 1200,
      isDesktop: w >= 1200,
      isUltraWide: w >= 1920,
      isMobile: w < 576,
      isTabletUp: w >= 576,
      isDesktopUp: w >= 992,
      isIOS,
      isAndroid,
      hasNotch: isIOS && w >= 375 && h >= 812,
      hasDynIsland: isIOS && w >= 393 && h >= 852,
      isLandscape: w > h,
      isLandscapePhone: w > h && h < 500,
      isTouch: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      isRetina: window.devicePixelRatio >= 2,
      w,
      h,
    };
  }, [windowWidth, windowHeight]);

  // ═══════════════════════════════════════════
  // RESPONSIVE SPACING & FONTS
  // ═══════════════════════════════════════════
  const sp = useMemo(() => {
    if (dev.isGalaxyFold) return { p: "0.3rem", g: "0.3rem", cp: "0.4rem", r: "10px" };
    if (dev.isMiniPhone) return { p: "0.4rem", g: "0.4rem", cp: "0.5rem", r: "12px" };
    if (dev.isSmallPhone) return { p: "0.5rem", g: "0.5rem", cp: "0.6rem", r: "14px" };
    if (dev.isMedPhone) return { p: "0.6rem", g: "0.5rem", cp: "0.75rem", r: "14px" };
    if (dev.isLargePhone) return { p: "0.7rem", g: "0.6rem", cp: "0.85rem", r: "16px" };
    if (dev.isSmallTablet) return { p: "0.8rem", g: "0.65rem", cp: "1rem", r: "16px" };
    if (dev.isTablet) return { p: "1rem", g: "0.75rem", cp: "1.15rem", r: "18px" };
    if (dev.isLargeTablet) return { p: "1.25rem", g: "0.85rem", cp: "1.25rem", r: "20px" };
    return { p: "1.5rem", g: "1rem", cp: "1.5rem", r: "22px" };
  }, [dev]);

  const fs = useMemo(() => {
    if (dev.isGalaxyFold)
      return {
        xxs: "0.48rem", xs: "0.55rem", sm: "0.62rem", md: "0.72rem",
        lg: "0.85rem", xl: "1rem", stat: "1rem", icon: "0.8rem",
      };
    if (dev.isMiniPhone)
      return {
        xxs: "0.52rem", xs: "0.58rem", sm: "0.65rem", md: "0.75rem",
        lg: "0.88rem", xl: "1.05rem", stat: "1.15rem", icon: "0.85rem",
      };
    if (dev.isSmallPhone)
      return {
        xxs: "0.55rem", xs: "0.62rem", sm: "0.68rem", md: "0.8rem",
        lg: "0.92rem", xl: "1.1rem", stat: "1.35rem", icon: "0.95rem",
      };
    if (dev.isMedPhone)
      return {
        xxs: "0.58rem", xs: "0.65rem", sm: "0.72rem", md: "0.84rem",
        lg: "0.96rem", xl: "1.12rem", stat: "1.5rem", icon: "1.05rem",
      };
    if (dev.isLargePhone)
      return {
        xxs: "0.6rem", xs: "0.68rem", sm: "0.75rem", md: "0.88rem",
        lg: "1rem", xl: "1.15rem", stat: "1.65rem", icon: "1.1rem",
      };
    if (dev.isSmallTablet)
      return {
        xxs: "0.62rem", xs: "0.7rem", sm: "0.76rem", md: "0.88rem",
        lg: "1.02rem", xl: "1.25rem", stat: "1.75rem", icon: "1.15rem",
      };
    if (dev.isTablet)
      return {
        xxs: "0.65rem", xs: "0.72rem", sm: "0.8rem", md: "0.9rem",
        lg: "1.08rem", xl: "1.35rem", stat: "1.9rem", icon: "1.25rem",
      };
    if (dev.isLargeTablet)
      return {
        xxs: "0.68rem", xs: "0.75rem", sm: "0.82rem", md: "0.92rem",
        lg: "1.1rem", xl: "1.4rem", stat: "2.1rem", icon: "1.35rem",
      };
    return {
      xxs: "0.72rem", xs: "0.78rem", sm: "0.85rem", md: "0.95rem",
      lg: "1.12rem", xl: "1.5rem", stat: "2.4rem", icon: "1.5rem",
    };
  }, [dev]);

  // ═══════════════════════════════════════════
  // THEME
  // ═══════════════════════════════════════════
  const t = {
    bg: "#f0f4f8", bg2: "#eef3fb", card: "#ffffff",
    text: "#374151", bright: "#0d1b2a", muted: "#64748b",
    dim: "#64748b", faint: "#94a3b8",
    pri: "#0B3D91", priDk: "#093278", priLt: "#0B3D91",
    priGlow: "rgba(11,61,145,0.18)",
    suc: "#16a34a", sucLt: "#16a34a", sucGlow: "rgba(22,163,74,0.18)",
    warn: "#d97706", warnLt: "#d97706", warnGlow: "rgba(217,119,6,0.18)",
    dan: "#dc2626", danLt: "#dc2626", danGlow: "rgba(220,38,38,0.18)",
    info: "#0369a1", infoLt: "#0369a1", infoGlow: "rgba(3,105,161,0.18)",
    bdr: "#e2e8f0", bdr2: "#f1f5f9", bdr3: "#f0f4fb",
    g1: "linear-gradient(135deg, #0B3D91, #093278)",
    g2: "linear-gradient(135deg, #16a34a, #15803d)",
    g3: "linear-gradient(135deg, #0369a1, #075985)",
    g4: "linear-gradient(135deg, #d97706, #b45309)",
  };

  // Field definitions for the 5 boolean fields
  const boolFields = [
    { key: "ufite_ubwiherero", label: "Ubwiherero", shortLabel: "Ubwih.", icon: "droplet-half", color: t.suc },
    { key: "ufite_ahobojyera", label: "Ahobojyera", shortLabel: "Ahob.", icon: "water", color: t.info },
    { key: "ufite_ubukarabiro_bwo_kurituwarete", label: "Ubukarabiro", shortLabel: "Ubuk.", icon: "moisture", color: t.warn },
    { key: "ufite_ingarani", label: "Ingarani", shortLabel: "Ingar.", icon: "shield-check", color: t.priLt },
    { key: "inzu_ikoze_amasuku", label: "Amasuku", shortLabel: "Amas.", icon: "house-check", color: t.sucLt },
  ];

  // ═══════════════════════════════════════════
  // INFINITE SCROLL
  // ═══════════════════════════════════════════
  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && visibleCount < filteredRecords.length) {
          setVisibleCount((prev) => Math.min(prev + 30, filteredRecords.length));
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loading, visibleCount]
  );

  // ═══════════════════════════════════════════
  // LOGIC
  // ═══════════════════════════════════════════
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_URL, config);
      setRecords(res.data || []);
    } catch {
      const demo = [
        { id: 1, amazina_y_umuturage: "Kalisa Jean", ufite_ubwiherero: "Yego", ufite_ahobojyera: "Yego", ufite_ubukarabiro_bwo_kurituwarete: "Yego", ufite_ingarani: "Yego", inzu_ikoze_amasuku: "Yego" },
        { id: 2, amazina_y_umuturage: "Mukamana Sarah", ufite_ubwiherero: "Oya", ufite_ahobojyera: "Yego", ufite_ubukarabiro_bwo_kurituwarete: "Oya", ufite_ingarani: "Oya", inzu_ikoze_amasuku: "Yego" },
      ];
      setRecords(demo);
      showAlertMsg("warning", "Demo Mode: API ntikunda kugerwa.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showAlertMsg = (variant, message) => {
    setAlertInfo({ show: true, variant, message });
    setTimeout(() => setAlertInfo({ show: false, variant: "", message: "" }), 4000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCitizenSelect = (e) => {
    const citizenId = e.target.value;
    const citizen = householdHeads.find((c) => String(c.umuturage_id) === String(citizenId));
    setFormData((prev) => ({
      ...prev,
      citizen_id: citizenId,
      amazina_y_umuturage: citizen ? citizen.full_name : "",
    }));
  };

  const resetForm = () => {
    setFormData({
      amazina_y_umuturage: "",
      citizen_id: "",
      ufite_ubwiherero: "Yego",
      ufite_ahobojyera: "Yego",
      ufite_ubukarabiro_bwo_kurituwarete: "Yego",
      ufite_ingarani: "Yego",
      inzu_ikoze_amasuku: "Yego",
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amazina_y_umuturage.trim()) {
      showAlertMsg("warning", "Amazina y'umuturage ni ngombwa!");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API_URL}/${editingId}`, formData, config);
        showAlertMsg("success", "Amakuru yavuguruwe neza!");
      } else {
        await axios.post(API_URL, formData, config);
        showAlertMsg("success", "Isuku yanditswe neza!");
      }
      resetForm();
      fetchData();
    } catch {
      showAlertMsg("danger", "Habaye ikosa mu kubika amakuru.");
    }
  };

  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!recordToDelete) return;
    try {
      await axios.delete(`${API_URL}/${recordToDelete.id}`, config);
      setRecords((prev) => prev.filter((r) => r.id !== recordToDelete.id));
      showAlertMsg("success", "Amakuru yasibwe neza!");
    } catch {
      showAlertMsg("danger", "Habaye ikosa mu gusiba.");
    } finally {
      setShowDeleteModal(false);
      setRecordToDelete(null);
    }
  };

  const handleEdit = (item) => {
    setFormData({ ...item });
    setEditingId(item.id);
    setShowForm(true);
    setShowMobileMenu(false);
    if (!dev.isMobile) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetail = (item) => {
    setSelectedRecord(item);
    setShowDetailModal(true);
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const exportCSV = () => {
    const headers = ["Amazina", "Ubwiherero", "Ahobojyera", "Ubukarabiro", "Ingarani", "Amasuku"];
    const rows = filteredRecords.map((r) => [
      r.amazina_y_umuturage,
      r.ufite_ubwiherero,
      r.ufite_ahobojyera,
      r.ufite_ubukarabiro_bwo_kurituwarete,
      r.ufite_ingarani,
      r.inzu_ikoze_amasuku,
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `isuku_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    showAlertMsg("success", "Excel yakoboye neza!");
    setShowMobileMenu(false);
  };

  // ═══════════════════════════════════════════
  // COMPUTED
  // ═══════════════════════════════════════════
  const filteredRecords = useMemo(
    () => records.filter((r) => r.amazina_y_umuturage?.toLowerCase().includes(searchTerm.toLowerCase())),
    [records, searchTerm]
  );

  const visibleData = useMemo(
    () => filteredRecords.slice(0, visibleCount),
    [filteredRecords, visibleCount]
  );

  const statCards = useMemo(() => [
    {
      title: dev.isMobile ? "Ingo" : "Ingo Zose",
      val: records.length, grad: t.g1, clr: t.priLt,
      icon: "house-door-fill", pct: 100,
    },
    {
      title: dev.isMobile ? "Ubwiherero" : "Bafite Ubwiherero",
      val: records.filter((r) => r.ufite_ubwiherero === "Yego").length,
      grad: t.g2, clr: t.sucLt, icon: "droplet-half",
      pct: records.length ? Math.round((records.filter((r) => r.ufite_ubwiherero === "Yego").length / records.length) * 100) : 0,
    },
    {
      title: dev.isMobile ? "Ahobojyera" : "Bafite Ahobojyera",
      val: records.filter((r) => r.ufite_ahobojyera === "Yego").length,
      grad: t.g3, clr: t.infoLt, icon: "water",
      pct: records.length ? Math.round((records.filter((r) => r.ufite_ahobojyera === "Yego").length / records.length) * 100) : 0,
    },
    {
      title: dev.isMobile ? "Ingarani" : "Bafite Ingarani",
      val: records.filter((r) => r.ufite_ingarani === "Yego").length,
      grad: t.g4, clr: t.warnLt, icon: "shield-check",
      pct: records.length ? Math.round((records.filter((r) => r.ufite_ingarani === "Yego").length / records.length) * 100) : 0,
    },
  ], [records, dev.isMobile]);

  // Count yes for a record (hygiene score)
  const getScore = (r) =>
    boolFields.filter((bf) => r[bf.key] === "Yego").length;

  // ═══════════════════════════════════════════
  // SUB-COMPONENTS
  // ═══════════════════════════════════════════
  const StatusPill = ({ val, small }) => {
    const yes = val === "Yego";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.2rem",
          padding: `0.15rem ${small || dev.isGalaxyFold ? "0.3rem" : "0.5rem"}`,
          borderRadius: "14px",
          fontSize: small ? fs.xxs : fs.xs,
          fontWeight: 600,
          color: yes ? t.suc : t.dan,
          background: yes ? "rgba(0,214,143,0.12)" : "rgba(255,61,113,0.12)",
          border: `1px solid ${yes ? "rgba(0,214,143,0.25)" : "rgba(255,61,113,0.25)"}`,
        }}
      >
        <i className={`bi bi-${yes ? "check-circle" : "x-circle"}`} style={{ fontSize: "0.65em" }} />
        {!dev.isGalaxyFold && val}
      </span>
    );
  };

  const ScoreBadge = ({ record }) => {
    const score = getScore(record);
    const pct = Math.round((score / 5) * 100);
    const clr = pct >= 80 ? t.suc : pct >= 60 ? t.warn : t.dan;
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.2rem",
          fontSize: dev.isGalaxyFold ? "0.5rem" : fs.xs,
          fontWeight: 700,
          color: clr,
          background: `${clr}15`,
          padding: `0.15rem ${dev.isGalaxyFold ? "0.25rem" : "0.4rem"}`,
          borderRadius: "10px",
          border: `1px solid ${clr}30`,
        }}
      >
        {score}/5
      </span>
    );
  };

  // ═══════════════════════════════════════════
  // MOBILE CARD
  // ═══════════════════════════════════════════
  const MobileCard = React.memo(({ item, index, isLast }) => {
    const isOpen = expandedRows.has(item.id);
    const score = getScore(item);
    const scorePct = Math.round((score / 5) * 100);
    const scoreClr = scorePct >= 80 ? t.suc : scorePct >= 60 ? t.warn : t.dan;

    return (
      <div
        ref={isLast ? lastItemRef : null}
        style={{
          background: `linear-gradient(145deg, ${t.card}, ${t.bg2})`,
          border: `1px solid ${isOpen ? t.pri : t.bdr}`,
          borderRadius: sp.r,
          marginBottom: sp.g,
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: isOpen ? `0 6px 24px ${t.pri}18` : "0 1px 3px rgba(11,61,145,0.08)",
          animation: "fadeIn 0.3s ease",
        }}
      >
        {/* HEADER */}
        <div
          onClick={() => toggleRow(item.id)}
          style={{
            padding: sp.cp,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: dev.isGalaxyFold ? "0.25rem" : "0.45rem",
            minHeight: dev.isTouch ? 48 : "auto",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <span
            style={{
              background: t.g1,
              color: "#ffffff",
              borderRadius: "50%",
              width: dev.isGalaxyFold ? 18 : dev.isMiniPhone ? 22 : 26,
              height: dev.isGalaxyFold ? 18 : dev.isMiniPhone ? 22 : 26,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: dev.isGalaxyFold ? "0.45rem" : fs.xxs,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {index + 1}
          </span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                color: t.bright,
                fontSize: dev.isGalaxyFold ? "0.68rem" : fs.md,
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: 1.3,
              }}
            >
              {item.amazina_y_umuturage || "—"}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                marginTop: "0.05rem",
                flexWrap: "wrap",
              }}
            >
              <ScoreBadge record={item} />
              {/* Mini dots for each field */}
              <div style={{ display: "flex", gap: "2px", marginLeft: "0.15rem" }}>
                {boolFields.map((bf) => (
                  <div
                    key={bf.key}
                    style={{
                      width: dev.isGalaxyFold ? 5 : dev.isMobile ? 7 : 8,
                      height: dev.isGalaxyFold ? 5 : dev.isMobile ? 7 : 8,
                      borderRadius: "50%",
                      background: item[bf.key] === "Yego" ? bf.color : "#cbd5e1",
                      transition: "all 0.3s",
                      boxShadow: item[bf.key] === "Yego" ? `0 0 4px ${bf.color}50` : "none",
                    }}
                    title={bf.label}
                  />
                ))}
              </div>
            </div>
          </div>

          <i
            className={`bi bi-chevron-${isOpen ? "up" : "down"}`}
            style={{ color: t.muted, fontSize: fs.sm, flexShrink: 0 }}
          />
        </div>

        {/* EXPANDED */}
        {isOpen && (
          <div style={{ borderTop: `1px solid ${t.bdr3}`, animation: "fadeIn 0.25s ease" }}>
            {/* Score bar */}
            <div style={{ padding: `0.5rem ${sp.cp}` }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.25rem",
                }}
              >
                <span style={{ color: t.dim, fontSize: fs.xxs, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                  Isuku Score
                </span>
                <span style={{ color: scoreClr, fontSize: fs.xs, fontWeight: 700 }}>
                  {score}/5 ({scorePct}%)
                </span>
              </div>
              <div
                style={{
                  height: dev.isGalaxyFold ? 3 : 5,
                  borderRadius: 5,
                  background: "rgba(255,255,255,0.06)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${scorePct}%`,
                    borderRadius: 5,
                    background: scoreClr,
                    transition: "width 0.6s ease",
                  }}
                />
              </div>
            </div>

            {/* Fields grid */}
            <div
              style={{
                padding: `0 ${sp.cp} ${sp.cp}`,
                display: "grid",
                gridTemplateColumns: dev.isGalaxyFold ? "1fr" : "1fr 1fr",
                gap: dev.isGalaxyFold ? "0.25rem" : "0.4rem",
              }}
            >
              {boolFields.map((bf) => (
                <div
                  key={bf.key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: dev.isGalaxyFold ? "0.25rem 0.3rem" : "0.35rem 0.5rem",
                    borderRadius: "8px",
                    background: item[bf.key] === "Yego" ? `${bf.color}08` : `${t.dan}06`,
                    border: `1px solid ${item[bf.key] === "Yego" ? `${bf.color}20` : `${t.dan}15`}`,
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.2rem",
                      color: t.muted,
                      fontSize: dev.isGalaxyFold ? "0.48rem" : fs.xxs,
                    }}
                  >
                    <i className={`bi bi-${bf.icon}`} style={{ color: bf.color, fontSize: "0.7em" }} />
                    {dev.isGalaxyFold ? bf.shortLabel : bf.label}
                  </span>
                  <StatusPill val={item[bf.key]} small />
                </div>
              ))}
            </div>

            {/* Actions */}
            <div
              style={{
                borderTop: `1px solid ${t.bdr3}`,
                padding: sp.cp,
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: dev.isGalaxyFold ? "0.2rem" : "0.35rem",
              }}
            >
              {[
                { label: "Reba", icon: "eye", clr: t.infoLt, fn: () => handleViewDetail(item) },
                { label: "Hindura", icon: "pencil", clr: t.priLt, fn: () => handleEdit(item) },
                { label: "Siba", icon: "trash", clr: t.danLt, fn: () => handleDeleteClick(item) },
              ].map((a) => (
                <button
                  key={a.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    a.fn();
                  }}
                  style={{
                    background: `${a.clr}12`,
                    border: `1px solid ${a.clr}30`,
                    color: a.clr,
                    borderRadius: "10px",
                    padding: dev.isGalaxyFold ? "0.35rem" : dev.isMiniPhone ? "0.45rem" : "0.55rem",
                    cursor: "pointer",
                    fontSize: dev.isGalaxyFold ? "0.5rem" : fs.xs,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.2rem",
                    transition: "all 0.2s",
                    minHeight: dev.isTouch ? 44 : "auto",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  <i className={`bi bi-${a.icon}`} />
                  {!dev.isGalaxyFold && <span>{a.label}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  });

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════
  return (
    <div
      style={{
        background: t.bg,
        color: t.text,
        minHeight: "100vh",
        minHeight: "100dvh",
        padding: sp.p,
        paddingBottom: dev.isMobile ? "5rem" : sp.p,
        fontFamily: "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        WebkitOverflowScrolling: "touch",
        
      }}
    >
      {/* ═══ GLOBAL STYLES ═══ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800&display=swap');
        *,*::before,*::after {
          font-family:'Nunito Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
          box-sizing:border-box;
          -webkit-tap-highlight-color:transparent;
        }
        html { -webkit-text-size-adjust:100%; scroll-behavior:smooth; }
        body { -webkit-font-smoothing:antialiased; -moz-osx-font-smoothing:grayscale; overscroll-behavior:none; margin:0; }

        @keyframes fadeIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulseDanger {
          0%{box-shadow:0 0 0 0 rgba(255,61,113,0.4)}
          70%{box-shadow:0 0 0 12px rgba(255,61,113,0)}
          100%{box-shadow:0 0 0 0 rgba(255,61,113,0)}
        }

        @supports(padding:env(safe-area-inset-top)){
          .safe-wrap{padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom);padding-left:env(safe-area-inset-left);padding-right:env(safe-area-inset-right)}
          .fab-wrap{bottom:calc(96px + env(safe-area-inset-bottom));right:calc(12px + env(safe-area-inset-right))}
        }

        /* STAT CARDS */
        .sc{background:${t.card};border:1px solid ${t.bdr};position:relative;overflow:hidden;transition:all 0.35s cubic-bezier(0.4,0,0.2,1);-webkit-transform:translateZ(0)}
        .sc:active{transform:scale(0.97)}
        @media(hover:hover){.sc:hover{transform:translateY(-5px) scale(1.01);box-shadow:0 16px 32px rgba(0,0,0,0.3);border-color:${t.bdr2}}}

        /* TABLE */
        .tw{background:${t.card};border:1px solid ${t.bdr};overflow:hidden;box-shadow:0 10px 32px rgba(0,0,0,0.25)}
        .mt-tbl{width:100%;border-collapse:separate;border-spacing:0}
        .mt-tbl thead tr th{background:${t.bg2};color:${t.muted};font-weight:700;text-transform:uppercase;letter-spacing:0.08em;border-bottom:2px solid ${t.bdr2};position:sticky;top:0;z-index:10}
        .mt-tbl tbody tr{transition:all 0.2s;border-bottom:1px solid ${t.bdr}}
        @media(hover:hover){.mt-tbl tbody tr:hover td{background:rgba(11,61,145,0.06)}}
        .mt-tbl tbody tr:last-child{border-bottom:none}

        @media(max-width:300px){.tw{border-radius:10px}.mt-tbl thead th{font-size:0.45rem;padding:0.4rem 0.15rem}.mt-tbl tbody td{font-size:0.48rem;padding:0.4rem 0.15rem}}
        @media(min-width:301px) and (max-width:320px){.tw{border-radius:12px}.mt-tbl thead th{font-size:0.48rem;padding:0.45rem 0.2rem}.mt-tbl tbody td{font-size:0.52rem;padding:0.45rem 0.2rem}}
        @media(min-width:321px) and (max-width:375px){.tw{border-radius:14px}.mt-tbl thead th{font-size:0.5rem;padding:0.5rem 0.25rem}.mt-tbl tbody td{font-size:0.55rem;padding:0.5rem 0.25rem}}
        @media(min-width:376px) and (max-width:413px){.tw{border-radius:15px}.mt-tbl thead th{font-size:0.52rem;padding:0.55rem 0.3rem}.mt-tbl tbody td{font-size:0.6rem;padding:0.55rem 0.3rem}}
        @media(min-width:414px) and (max-width:575px){.tw{border-radius:16px}.mt-tbl thead th{font-size:0.55rem;padding:0.6rem 0.3rem}.mt-tbl tbody td{font-size:0.65rem;padding:0.6rem 0.3rem}}
        @media(min-width:576px) and (max-width:767px){.tw{border-radius:17px}.mt-tbl thead th{font-size:0.58rem;padding:0.65rem 0.35rem}.mt-tbl tbody td{font-size:0.7rem;padding:0.65rem 0.35rem}}
        @media(min-width:768px) and (max-width:991px){.tw{border-radius:18px}.mt-tbl thead th{font-size:0.62rem;padding:0.75rem 0.4rem}.mt-tbl tbody td{font-size:0.75rem;padding:0.75rem 0.4rem}}
        @media(min-width:992px) and (max-width:1199px){.tw{border-radius:20px}.mt-tbl thead th{font-size:0.65rem;padding:0.85rem 0.5rem}.mt-tbl tbody td{font-size:0.78rem;padding:0.85rem 0.5rem}}
        @media(min-width:1200px){.tw{border-radius:22px}.mt-tbl thead th{font-size:0.75rem;padding:1.1rem 0.8rem}.mt-tbl tbody td{font-size:0.85rem;padding:1.1rem 0.8rem}}

        /* BUTTONS */
        .ba{display:inline-flex;align-items:center;justify-content:center;border-radius:10px;transition:all 0.2s;cursor:pointer;padding:0}
        .ba:active{transform:scale(0.93)}
        @media(hover:hover){.ba:hover{transform:scale(1.08)}}
        @media(hover:none) and (pointer:coarse){.ba{min-width:44px;min-height:44px}}
        @media(max-width:375px){.ba{width:28px;height:28px}}
        @media(min-width:376px) and (max-width:575px){.ba{width:30px;height:30px}}
        @media(min-width:576px) and (max-width:991px){.ba{width:32px;height:32px}}
        @media(min-width:992px){.ba{width:34px;height:34px}}

        .btn-m{border:none;border-radius:12px;font-weight:600;transition:all 0.3s;white-space:nowrap;cursor:pointer}
        .btn-m:active{transform:scale(0.96)}
        @media(hover:hover){.btn-m:hover{transform:translateY(-2px)}}
        .btn-g{background:transparent;border:1px solid ${t.bdr2};color:${t.text};border-radius:12px;transition:all 0.3s;cursor:pointer}
        .btn-g:active{background:${t.bg2}}
        @media(hover:hover){.btn-g:hover{border-color:${t.pri};color:${t.pri}}}

        /* FILTER */
        .fc{background:${t.card};border:1px solid ${t.bdr}}
        @media(max-width:375px){.fc{border-radius:12px}}
        @media(min-width:376px) and (max-width:767px){.fc{border-radius:14px}}
        @media(min-width:768px){.fc{border-radius:16px}}
        .si{background:${t.bg2} !important;border:1px solid ${t.bdr} !important;color:${t.text} !important;transition:all 0.3s}
        .si:focus{border-color:${t.pri} !important;box-shadow:0 0 0 3px rgba(11,61,145,0.25) !important}
        .si::placeholder{color:${t.muted} !important;opacity:0.6}
        @media(max-width:575px){.si,input,select,textarea,.form-control,.form-select{font-size:16px !important}}

        /* MODALS */
        .modal-content{background:${t.card} !important;border:1px solid ${t.bdr} !important;color:${t.text} !important}
        @media(max-width:575px){.modal-content{border-radius:20px 20px 0 0 !important;margin:0 !important}.modal-dialog{margin:0 !important;max-width:100% !important}.modal-body{max-height:68vh;overflow-y:auto;-webkit-overflow-scrolling:touch}}
        @media(min-width:576px){.modal-content{border-radius:20px !important}}
        @media(min-width:992px){.modal-content{border-radius:24px !important}}
        .modal-header{border-bottom:1px solid ${t.bdr3} !important}
        .modal-footer{border-top:1px solid ${t.bdr3} !important}
        @media(max-width:575px){.modal-header{padding:1rem !important}.modal-body{padding:0.85rem !important}.modal-footer{padding:0.85rem !important}.modal-title{font-size:0.92rem !important}}
        @media(min-width:576px) and (max-width:991px){.modal-header{padding:1.25rem !important}.modal-body{padding:1.25rem !important}.modal-footer{padding:1rem !important}}
        @media(min-width:992px){.modal-header{padding:1.5rem !important}.modal-body{padding:1.5rem !important}.modal-footer{padding:1.25rem !important}}

        /* FORM */
        .form-control,.form-select{background:${t.bg2} !important;border:1px solid ${t.bdr} !important;color:${t.text} !important}
        .form-control:focus,.form-select:focus{border-color:${t.pri} !important;box-shadow:0 0 0 3px rgba(11,61,145,0.25) !important}
        .form-control::placeholder{color:${t.muted};opacity:0.6}
        .form-select option{background:${t.card};color:${t.text}}
        .form-label{color:${t.text} !important;font-weight:600 !important}
        @media(max-width:575px){.form-control,.form-select{font-size:16px !important;border-radius:10px !important;padding:0.6rem 0.8rem !important}.form-label{font-size:0.8rem !important;margin-bottom:0.35rem !important}}
        @media(min-width:576px){.form-control,.form-select{border-radius:12px !important;padding:0.7rem 1rem !important}.form-label{font-size:0.85rem !important}}
        .input-group-text{background:${t.bg2} !important;border-color:${t.bdr} !important;color:${t.muted} !important}
        @media(max-width:575px){.input-group-text{border-radius:10px 0 0 10px !important}}
        @media(min-width:576px){.input-group-text{border-radius:12px 0 0 12px !important}}

        .alert{border:none !important}
        @media(max-width:575px){.alert{border-radius:12px !important;font-size:0.8rem !important}}
        @media(min-width:576px){.alert{border-radius:14px !important}}

        .tooltip-inner{background:#f0f4fb !important;border:1px solid ${t.bdr} !important;border-radius:8px !important}
        .tooltip .tooltip-arrow::before{border-top-color:#f0f4fb !important}

        ::-webkit-scrollbar{width:6px;height:6px}
        ::-webkit-scrollbar-track{background:${t.bg}}
        ::-webkit-scrollbar-thumb{background:${t.bdr};border-radius:3px}
        ::-webkit-scrollbar-thumb:hover{background:${t.bdr2}}
        *{scrollbar-width:thin;scrollbar-color:${t.bdr} ${t.bg}}

        @media(max-height:500px) and (orientation:landscape){.modal-body{max-height:50vh;overflow-y:auto}}

        /* FAB */
        .fab-wrap{position:fixed;bottom:96px;right:12px;z-index:100000}
        .fab-btn{width:54px;height:54px;border-radius:50%;background:linear-gradient(135deg,${t.pri},${t.priDk});border:none;color:#ffffff;font-size:1.4rem;display:flex;align-items:center;justify-content:center;box-shadow:0 6px 20px ${t.priGlow};cursor:pointer;transition:all 0.3s}
        .fab-btn:active{transform:scale(0.88)}
        .fab-m{position:absolute;bottom:64px;right:0;background:${t.card};border:1px solid ${t.bdr};border-radius:14px;padding:0.4rem;min-width:170px;animation:slideUp 0.2s ease;box-shadow:0 16px 32px rgba(0,0,0,0.5)}
        .fab-mi{display:flex;align-items:center;gap:0.65rem;padding:0.65rem 0.75rem;border-radius:9px;color:${t.muted};background:none;border:none;width:100%;font-size:0.82rem;font-weight:500;cursor:pointer;transition:all 0.2s}
        .fab-mi:active{background:rgba(11,61,145,0.14);color:#0d1b2a}
        @media(hover:hover){.fab-mi:hover{background:rgba(11,61,145,0.1);color:#0d1b2a}}

        @media print{body{background:#0d1b2a !important;color:#ffffff !important}.fab-wrap,.btn-m,.btn-g,.fc{display:none !important}}
      `}</style>

      <div className="safe-wrap">
        {/* ═══ HEADER ═══ */}
        <div
          style={{
            background: "linear-gradient(135deg, #0B3D91 0%, #093278 60%, #0B3D91 100%)",
            border: `1px solid ${t.bdr3}`,
            borderRadius: dev.isMobile ? "14px" : "20px",
            padding: dev.isGalaxyFold ? "0.7rem" : dev.isMobile ? "0.9rem 1rem" : "1.4rem 1.8rem",
            marginBottom: sp.g,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          }}
        >
          <div
            style={{
              position: "absolute", top: "-40px", right: "-40px",
              width: 180, height: 180, borderRadius: "50%",
              background: "radial-gradient(circle, rgba(11,61,145,0.18) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: dev.isMobile ? "0.65rem" : "1rem", minWidth: 0, zIndex: 1 }}>
            <div
              style={{
                width: dev.isGalaxyFold ? 36 : dev.isMobile ? 42 : 52,
                height: dev.isGalaxyFold ? 36 : dev.isMobile ? 42 : 52,
                borderRadius: dev.isMobile ? "12px" : "16px",
                background: "linear-gradient(135deg, #0B3D91 0%, #093278 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 6px 20px ${t.priGlow}`,
                flexShrink: 0,
              }}
            >
              <i
                className="bi bi-droplet-half"
                style={{ color: "#ffffff", fontSize: dev.isGalaxyFold ? "0.9rem" : dev.isMobile ? "1.1rem" : "1.4rem" }}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2
                className="fw-bold mb-0"
                style={{
                  color: "#ffffff",
                  fontSize: dev.isGalaxyFold ? "0.8rem" : dev.isMobile ? fs.lg : dev.isTablet ? "1.4rem" : "1.85rem",
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                }}
              >
                {dev.isMobile ? "Isuku" : "Isuku y'Urugo"}
              </h2>
              {!dev.isGalaxyFold && (
                <p style={{ color: "rgba(255,255,255,0.75)", margin: "2px 0 0", fontSize: fs.sm, lineHeight: 1.4 }}>
                  Gucunga isuku n'isukura •{" "}
                  <strong style={{ color: "#ffffff" }}>{filteredRecords.length}</strong> records
                </p>
              )}
            </div>
          </div>

          {!dev.isMobile && (
            <div className="d-flex gap-2 flex-wrap" style={{ zIndex: 1 }}>
              <button
                className="btn-m"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                style={{
                  background: "linear-gradient(135deg, #0B3D91 0%, #093278 100%)",
                  color: "#ffffff",
                  padding: dev.isTablet ? "0.55rem 1rem" : "0.6rem 1.4rem",
                  fontSize: fs.sm,
                  boxShadow: `0 4px 20px ${t.priGlow}`,
                }}
              >
                <i className="bi bi-plus-circle me-2" />
                Ongeraho
              </button>
              <button
                onClick={exportCSV}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: `1px solid ${t.bdr3}`,
                  borderRadius: "50px",
                  color: "rgba(255,255,255,0.85)",
                  padding: dev.isTablet ? "0.5rem 0.75rem" : "0.6rem 1rem",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  fontSize: fs.sm,
                  transition: "all 0.25s ease",
                }}
              >
                <i className="bi bi-download me-1" />
                <span className="d-none d-lg-inline">Excel</span>
              </button>
              <button
                onClick={fetchData}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: `1px solid ${t.bdr3}`,
                  borderRadius: "50px",
                  color: "rgba(255,255,255,0.85)",
                  padding: dev.isTablet ? "0.5rem 0.75rem" : "0.6rem 1rem",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  transition: "all 0.25s ease",
                }}
              >
                <i className="bi bi-arrow-clockwise" />
              </button>
            </div>
          )}
        </div>

        {/* ALERT */}
        {alertInfo.show && (
          <Alert
            variant={alertInfo.variant}
            dismissible
            onClose={() => setAlertInfo({ show: false, variant: "", message: "" })}
            className="mb-3 shadow"
          >
            {alertInfo.message}
          </Alert>
        )}

        {/* ═══ STATS ═══ */}
        <Row className="g-2 g-md-3" style={{ marginBottom: sp.g }}>
          {statCards.map((s, i) => {
            const boxSize =
              dev.isGalaxyFold ? 28 : dev.isMiniPhone ? 34 : dev.isSmallPhone ? 40 : dev.isMedPhone ? 44 : dev.isLargePhone ? 48 : dev.isSmallTablet ? 48 : dev.isTablet ? 52 : 56;
            return (
              <Col key={i} xs={6} md={3}>
                <div className="sc" style={{ borderRadius: sp.r, padding: sp.cp }}>
                  <div
                    style={{
                      position: "absolute",
                      top: -25,
                      right: -25,
                      width: dev.isMobile ? 70 : 120,
                      height: dev.isMobile ? 70 : 120,
                      borderRadius: "50%",
                      background: s.grad,
                      opacity: 0.1,
                      filter: "blur(25px)",
                    }}
                  />
                  <div className="d-flex align-items-start justify-content-between mb-2" style={{ position: "relative" }}>
                    <div
                      style={{
                        width: boxSize,
                        height: boxSize,
                        borderRadius: dev.isGalaxyFold ? "8px" : dev.isMobile ? "10px" : "16px",
                        background: s.grad,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 6px 16px ${s.clr}30`,
                      }}
                    >
                      <i className={`bi bi-${s.icon}`} style={{ fontSize: fs.icon, color: "#ffffff" }} />
                    </div>
                    <div
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        borderRadius: "12px",
                        padding: `0.1rem ${dev.isGalaxyFold ? "0.25rem" : "0.45rem"}`,
                        fontSize: dev.isGalaxyFold ? "0.42rem" : fs.xxs,
                        fontWeight: 700,
                        color: s.clr,
                      }}
                    >
                      {s.pct}%
                    </div>
                  </div>
                  <h3
                    style={{
                      fontSize: fs.stat,
                      fontWeight: 800,
                      color: t.bright,
                      marginBottom: "0.1rem",
                      lineHeight: 1.1,
                      letterSpacing: "-0.03em",
                      textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                      position: "relative",
                    }}
                  >
                    {s.val}
                  </h3>
                  <p
                    style={{
                      color: t.muted,
                      fontSize: dev.isGalaxyFold ? "0.42rem" : fs.xs,
                      fontWeight: 500,
                      margin: 0,
                      position: "relative",
                    }}
                  >
                    {s.title}
                  </p>
                  <div
                    style={{
                      marginTop: dev.isMobile ? "0.35rem" : "0.6rem",
                      height: dev.isGalaxyFold ? 2 : 4,
                      borderRadius: 4,
                      background: "rgba(255,255,255,0.06)",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${s.pct}%`,
                        borderRadius: 4,
                        background: s.grad,
                        transition: "width 1s ease",
                      }}
                    />
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>

        {/* ═══ FORM (Desktop inline) ═══ */}
        {showForm && !dev.isMobile && (
          <div
            style={{
              background: t.card,
              borderRadius: sp.r,
              border: `1px solid ${t.bdr}`,
              marginBottom: sp.g,
              overflow: "hidden",
              animation: "fadeIn 0.3s ease",
            }}
          >
            <div
              style={{
                background: `linear-gradient(135deg, ${t.pri}, ${t.priDk})`,
                color: "#ffffff",
                padding: "1rem 1.5rem",
              }}
            >
              <h5 className="mb-0 fw-bold" style={{ fontSize: fs.lg }}>
                <i className={`bi ${editingId ? "bi-pencil-square" : "bi-plus-circle"} me-2`} />
                {editingId ? "Hindura Isuku" : "Andika Isuku Nshya"}
              </h5>
            </div>
            <div style={{ padding: sp.cp }}>
              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Amazina y'Umuturage *</Form.Label>
                      <Form.Select
                        name="citizen_id"
                        value={formData.citizen_id || ""}
                        onChange={handleCitizenSelect}
                        required
                      >
                        <option value="">-- Hitamo umuturage --</option>
                        {householdHeads.map((c) => (
                          <option key={c.umuturage_id} value={c.umuturage_id}>
                            {c.full_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {boolFields.map((bf) => (
                    <Col md={4} lg={2} key={bf.key}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: fs.sm }}>
                          <i className={`bi bi-${bf.icon} me-1`} style={{ color: bf.color }} />
                          {bf.label}
                        </Form.Label>
                        <Form.Select name={bf.key} value={formData[bf.key]} onChange={handleInputChange} required>
                          <option value="Yego">Yego</option>
                          <option value="Oya">Oya</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  ))}
                  <Col md={12} className="d-flex justify-content-end gap-2 mt-2">
                    <button type="button" onClick={resetForm} className="btn-g" style={{ padding: "0.55rem 1.2rem", fontWeight: 600 }}>
                      <i className="bi bi-x-circle me-1" />
                      Hagarika
                    </button>
                    <button
                      type="submit"
                      className="btn-m"
                      style={{
                        background: t.suc,
                        color: "#ffffff",
                        padding: "0.55rem 1.4rem",
                        fontWeight: 700,
                        boxShadow: `0 4px 14px rgba(0,214,143,0.3)`,
                      }}
                    >
                      <i className="bi bi-check-circle me-1" />
                      {editingId ? "Vugurura" : "Bika"}
                    </button>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        )}

        {/* ═══ SEARCH ═══ */}
        <div className="fc" style={{ padding: sp.cp, marginBottom: sp.g }}>
          <InputGroup>
            <InputGroup.Text style={{ borderRadius: "10px 0 0 10px" }}>
              <i className="bi bi-search" style={{ fontSize: fs.sm }} />
            </InputGroup.Text>
            <Form.Control
              placeholder={dev.isMobile ? "Shakisha..." : "Shakisha amazina y'umuturage..."}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setVisibleCount(40);
              }}
              className="si"
              style={{ borderRadius: "0 10px 10px 0" }}
            />
            {searchTerm && (
              <Button
                variant="link"
                onClick={() => setSearchTerm("")}
                style={{
                  position: "absolute",
                  right: 4,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 5,
                  background: "none",
                  border: "none",
                  color: t.muted,
                  padding: "0.4rem",
                }}
              >
                <i className="bi bi-x-lg" />
              </Button>
            )}
          </InputGroup>
          {searchTerm && (
            <div className="mt-2">
              <small style={{ color: t.muted, fontSize: fs.xxs }}>
                <i className="bi bi-funnel me-1" />
                Byasanze: <strong style={{ color: t.pri }}>{filteredRecords.length}</strong> / {records.length}
              </small>
            </div>
          )}
        </div>

        {/* DATA COUNT */}
        {!loading && filteredRecords.length > 0 && (
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ marginBottom: sp.g, padding: "0 0.15rem" }}
          >
            <small style={{ color: t.muted, fontSize: fs.xxs }}>
              <i className="bi bi-database me-1" />
              {dev.isMobile
                ? `${visibleData.length}/${filteredRecords.length}`
                : `Erekana ${visibleData.length} muri ${filteredRecords.length}`}
            </small>
            {visibleCount < filteredRecords.length && (
              <Button
                size="sm"
                variant="link"
                onClick={() => setVisibleCount(filteredRecords.length)}
                style={{ color: t.pri, fontSize: fs.xxs, padding: 0, textDecoration: "none" }}
              >
                Byose ▼
              </Button>
            )}
          </div>
        )}

        {/* ═══ MAIN CONTENT ═══ */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner
              animation="border"
              style={{
                color: t.pri,
                width: dev.isMobile ? "2rem" : "3rem",
                height: dev.isMobile ? "2rem" : "3rem",
              }}
            />
            <div className="mt-3" style={{ color: t.muted, fontSize: fs.md }}>
              Ibyanditswe biragarurwa...
            </div>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div
            className="text-center py-5"
            style={{
              background: t.card,
              borderRadius: sp.r,
              border: `1px dashed ${t.bdr}`,
              padding: dev.isMobile ? "2rem 1rem" : "3rem 1.5rem",
            }}
          >
            <i className="bi bi-stars" style={{ fontSize: dev.isMobile ? "2.5rem" : "4rem", color: t.bdr2 }} />
            <h5 className="mt-3" style={{ color: t.muted, fontSize: fs.lg }}>
              {searchTerm ? "Nta muturage wahiswe" : "Nta makuru y'isuku ahari"}
            </h5>
            <p style={{ color: t.dim, fontSize: fs.sm }}>
              {searchTerm ? "Hindura ibyo ushakisha" : "Tangira ukongeramo"}
            </p>
            {!searchTerm && (
              <button
                className="btn-m mt-2"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                style={{
                  background: t.pri,
                  color: "#ffffff",
                  padding: "0.6rem 1rem",
                  fontSize: fs.sm,
                  boxShadow: `0 4px 14px rgba(11,61,145,0.3)`,
                }}
              >
                <i className="bi bi-plus-circle me-2" />
                Ongeraho
              </button>
            )}
          </div>
        ) : dev.isMobile ? (
          /* ═══ MOBILE: CARDS ═══ */
          <div ref={scrollRef}>
            {visibleData.map((item, i) => (
              <MobileCard key={item.id} item={item} index={i} isLast={i === visibleData.length - 1} />
            ))}
            {visibleCount < filteredRecords.length && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" style={{ color: t.pri }} />
                <small className="ms-2" style={{ color: t.muted, fontSize: fs.xxs }}>
                  Gutegura ibindi...
                </small>
              </div>
            )}
          </div>
        ) : (
          /* ═══ DESKTOP/TABLET: TABLE ═══ */
          <div className="tw" ref={scrollRef}>
            <div
              className="table-responsive"
              style={{
                WebkitOverflowScrolling: "touch",
                maxHeight: dev.isLandscapePhone ? "55vh" : "none",
              }}
            >
              <table className="mt-tbl">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center", width: 45 }}>#</th>
                    <th>Umuturage</th>
                    {boolFields.map((bf) => (
                      <th key={bf.key} style={{ textAlign: "center" }}>
                        {dev.isTablet && !dev.isDesktopUp ? bf.shortLabel : bf.label}
                      </th>
                    ))}
                    <th style={{ textAlign: "center" }}>Score</th>
                    <th style={{ textAlign: "center", width: dev.isTablet ? 100 : 120 }}>
                      Ibikorwa
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleData.map((record, idx) => (
                    <tr key={record.id} ref={idx === visibleData.length - 1 ? lastItemRef : null}>
                      <td style={{ textAlign: "center" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: dev.isTablet ? 24 : 28,
                            height: dev.isTablet ? 24 : 28,
                            borderRadius: "6px",
                            background: "rgba(11,61,145,0.1)",
                            color: t.pri,
                            fontWeight: 700,
                            fontSize: fs.xs,
                          }}
                        >
                          {idx + 1}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: dev.isTablet ? 28 : 34,
                              height: dev.isTablet ? 28 : 34,
                              borderRadius: "8px",
                              background: `linear-gradient(135deg, ${t.pri}33, ${t.pri}11)`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <i className="bi bi-person-fill" style={{ color: t.pri, fontSize: fs.icon }} />
                          </div>
                          <div
                            style={{
                              color: t.bright,
                              fontWeight: 600,
                              fontSize: fs.md,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: dev.isTablet ? 120 : dev.isLargeTablet ? 180 : "auto",
                            }}
                          >
                            {record.amazina_y_umuturage || "—"}
                          </div>
                        </div>
                      </td>
                      {boolFields.map((bf) => (
                        <td key={bf.key} style={{ textAlign: "center" }}>
                          <StatusPill val={record[bf.key]} />
                        </td>
                      ))}
                      <td style={{ textAlign: "center" }}>
                        <ScoreBadge record={record} />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="d-flex gap-1 justify-content-center">
                          <OverlayTrigger placement="top" overlay={<Tooltip>Reba</Tooltip>}>
                            <button
                              className="ba"
                              onClick={() => handleViewDetail(record)}
                              style={{
                                border: `1px solid ${t.info}30`,
                                background: `${t.info}08`,
                                color: t.info,
                              }}
                            >
                              <i className="bi bi-eye" />
                            </button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Hindura</Tooltip>}>
                            <button
                              className="ba"
                              onClick={() => handleEdit(record)}
                              style={{
                                border: `1px solid ${t.pri}30`,
                                background: `${t.pri}08`,
                                color: t.pri,
                              }}
                            >
                              <i className="bi bi-pencil" />
                            </button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Siba</Tooltip>}>
                            <button
                              className="ba"
                              onClick={() => handleDeleteClick(record)}
                              style={{
                                border: `1px solid ${t.dan}30`,
                                background: `${t.dan}08`,
                                color: t.dan,
                              }}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "0.75rem 1.2rem",
                background: "#f0f4fb",
                borderTop: `1px solid ${t.bdr}`,
              }}
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <small style={{ color: t.muted, fontSize: fs.xs }}>
                  Erekana: <strong style={{ color: t.text }}>{visibleData.length}</strong> /{" "}
                  <strong style={{ color: t.text }}>{filteredRecords.length}</strong>
                </small>
                {visibleCount < filteredRecords.length && (
                  <button
                    className="btn-g"
                    onClick={() => setVisibleCount(filteredRecords.length)}
                    style={{ padding: "0.3rem 0.8rem", fontSize: fs.xs }}
                  >
                    Byose ({filteredRecords.length})
                  </button>
                )}
              </div>
            </div>

            {visibleCount < filteredRecords.length && (
              <div className="text-center py-3" style={{ borderTop: `1px solid ${t.bdr3}` }}>
                <Spinner animation="border" size="sm" style={{ color: t.pri }} />
                <small className="ms-2" style={{ color: t.muted }}>
                  Gutegura...
                </small>
              </div>
            )}
          </div>
        )}

        {/* ═══ MOBILE FAB ═══ */}
        {dev.isMobile && (
          <div className="fab-wrap">
            {showMobileMenu && (
              <div className="fab-m">
                <button
                  className="fab-mi"
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                    setShowMobileMenu(false);
                  }}
                >
                  <i className="bi bi-plus-circle" style={{ color: t.suc, fontSize: "1.05rem" }} />
                  Ongeraho
                </button>
                <button
                  className="fab-mi"
                  onClick={() => {
                    fetchData();
                    setShowMobileMenu(false);
                  }}
                >
                  <i className="bi bi-arrow-clockwise" style={{ color: t.info, fontSize: "1.05rem" }} />
                  Fungura
                </button>
                <button className="fab-mi" onClick={exportCSV}>
                  <i className="bi bi-download" style={{ color: t.warn, fontSize: "1.05rem" }} />
                  Excel
                </button>
                {filteredRecords.length > visibleCount && (
                  <button
                    className="fab-mi"
                    onClick={() => {
                      setVisibleCount(filteredRecords.length);
                      setShowMobileMenu(false);
                    }}
                  >
                    <i className="bi bi-list-ul" style={{ color: t.priLt, fontSize: "1.05rem" }} />
                    Byose ({filteredRecords.length})
                  </button>
                )}
              </div>
            )}
            <button
              className="fab-btn"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              style={{
                transform: showMobileMenu ? "rotate(45deg)" : "none",
                transition: "transform 0.3s",
              }}
            >
              <i className="bi bi-plus-lg" />
            </button>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════
          ADD/EDIT MODAL (Mobile)
      ═══════════════════════════════════════════ */}
      {dev.isMobile && (
        <Modal show={showForm} onHide={resetForm} fullscreen scrollable>
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{
              background: `linear-gradient(135deg, ${t.pri}, ${t.priDk})`,
              border: "none",
            }}
          >
            <Modal.Title className="text-white" style={{ fontSize: "0.95rem" }}>
              <i className={`bi ${editingId ? "bi-pencil-square" : "bi-plus-circle"} me-2`} />
              {editingId ? "Hindura" : "Ongeraho Isuku"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: t.bg }}>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Amazina y'Umuturage *</Form.Label>
                <Form.Select
                  name="citizen_id"
                  value={formData.citizen_id || ""}
                  onChange={handleCitizenSelect}
                  required
                >
                  <option value="">-- Hitamo umuturage --</option>
                  {householdHeads.map((c) => (
                    <option key={c.umuturage_id} value={c.umuturage_id}>
                      {c.full_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {boolFields.map((bf) => (
                <Form.Group className="mb-3" key={bf.key}>
                  <Form.Label>
                    <i className={`bi bi-${bf.icon} me-1`} style={{ color: bf.color }} />
                    {bf.label}
                  </Form.Label>
                  <Form.Select name={bf.key} value={formData[bf.key]} onChange={handleInputChange} required>
                    <option value="Yego">Yego</option>
                    <option value="Oya">Oya</option>
                  </Form.Select>
                </Form.Group>
              ))}

              <div className="d-grid gap-2 mt-4">
                <button
                  type="submit"
                  className="btn-m"
                  style={{
                    background: editingId
                      ? `linear-gradient(135deg, ${t.warn}, #d97706)`
                      : `linear-gradient(135deg, ${t.suc}, #15803d)`,
                    color: "#ffffff",
                    padding: "0.75rem",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    boxShadow: `0 4px 14px ${editingId ? t.warn : t.suc}30`,
                  }}
                >
                  <i className={`bi me-1 ${editingId ? "bi-save" : "bi-check-circle"}`} />
                  {editingId ? "Vugurura" : "Bika"}
                </button>
                <button type="button" onClick={resetForm} className="btn-g" style={{ padding: "0.65rem", fontWeight: 600 }}>
                  <i className="bi bi-x-circle me-1" />
                  Funga
                </button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}

      {/* ═══════════════════════════════════════════
          DETAIL MODAL
      ═══════════════════════════════════════════ */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        centered={!dev.isMobile}
        fullscreen={dev.isMobile ? true : undefined}
        scrollable
        size={dev.isMobile ? undefined : "lg"}
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{
            background: `linear-gradient(135deg, ${t.info}, #075985)`,
            border: "none",
          }}
        >
          <Modal.Title className="text-white" style={{ fontSize: dev.isMobile ? "0.95rem" : "1.1rem" }}>
            <i className="bi bi-eye me-2" />
            Amakuru y'Isuku Arambuye
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: t.bg }}>
          {selectedRecord && (
            <div>
              {/* Name Card */}
              <div
                style={{
                  background: `linear-gradient(145deg, ${t.card}, ${t.bg2})`,
                  borderRadius: sp.r,
                  border: `1px solid ${t.bdr}`,
                  padding: sp.cp,
                  textAlign: "center",
                  marginBottom: sp.g,
                }}
              >
                <div
                  style={{
                    width: dev.isMobile ? 50 : 70,
                    height: dev.isMobile ? 50 : 70,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${t.pri}33, ${t.pri}11)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 0.6rem",
                  }}
                >
                  <i className="bi bi-person-fill" style={{ fontSize: dev.isMobile ? "1.5rem" : "2rem", color: t.pri }} />
                </div>
                <h4
                  style={{
                    color: t.bright,
                    fontWeight: 700,
                    fontSize: dev.isMobile ? fs.lg : "1.3rem",
                    marginBottom: "0.3rem",
                  }}
                >
                  {selectedRecord.amazina_y_umuturage}
                </h4>
                <ScoreBadge record={selectedRecord} />
              </div>

              {/* Score Progress */}
              <div
                style={{
                  padding: sp.cp,
                  background: `linear-gradient(145deg, ${t.card}, ${t.bg2})`,
                  borderRadius: sp.r,
                  border: `1px solid ${t.bdr}`,
                  marginBottom: sp.g,
                }}
              >
                {(() => {
                  const score = getScore(selectedRecord);
                  const pct = Math.round((score / 5) * 100);
                  const clr = pct >= 80 ? t.suc : pct >= 60 ? t.warn : t.dan;
                  return (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span style={{ color: t.muted, fontSize: fs.sm, fontWeight: 600 }}>
                          <i className="bi bi-speedometer2 me-1" />
                          Isuku Score
                        </span>
                        <span style={{ color: clr, fontSize: fs.lg, fontWeight: 800 }}>
                          {pct}%
                        </span>
                      </div>
                      <div
                        style={{
                          height: 8,
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.06)",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${pct}%`,
                            borderRadius: 8,
                            background: clr,
                            transition: "width 0.8s ease",
                          }}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Fields Grid */}
              <Row className="g-2">
                {boolFields.map((bf) => {
                  const yes = selectedRecord[bf.key] === "Yego";
                  return (
                    <Col xs={6} md={4} key={bf.key}>
                      <div
                        style={{
                          background: `linear-gradient(145deg, ${t.card}, ${t.bg2})`,
                          borderRadius: sp.r,
                          border: `1px solid ${yes ? `${bf.color}30` : `${t.dan}20`}`,
                          padding: dev.isMobile ? "0.65rem 0.5rem" : "1rem",
                          textAlign: "center",
                          transition: "all 0.3s",
                        }}
                      >
                        <i
                          className={`bi bi-${bf.icon}`}
                          style={{
                            fontSize: dev.isMobile ? "1.3rem" : "2rem",
                            color: yes ? bf.color : t.dan,
                            display: "block",
                            marginBottom: "0.3rem",
                          }}
                        />
                        <div
                          style={{
                            color: t.muted,
                            fontSize: dev.isMobile ? "0.6rem" : fs.sm,
                            fontWeight: 600,
                            marginBottom: "0.25rem",
                          }}
                        >
                          {bf.label}
                        </div>
                        <Badge
                          bg={yes ? "success" : "danger"}
                          style={{
                            fontSize: dev.isMobile ? "0.55rem" : fs.xs,
                            padding: "0.2rem 0.45rem",
                          }}
                        >
                          {yes ? "Yego" : "Oya"}
                        </Badge>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            flexDirection: dev.isMobile ? "column" : "row",
            gap: dev.isMobile ? "0.5rem" : undefined,
          }}
        >
          <button
            className="btn-m"
            onClick={() => {
              setShowDetailModal(false);
              if (selectedRecord) handleEdit(selectedRecord);
            }}
            style={{
              background: `linear-gradient(135deg, ${t.pri}, ${t.priDk})`,
              color: "#ffffff",
              padding: "0.6rem 1.2rem",
              fontWeight: 600,
              width: dev.isMobile ? "100%" : "auto",
            }}
          >
            <i className="bi bi-pencil-square me-1" />
            Hindura
          </button>
          <button
            className="btn-g"
            onClick={() => setShowDetailModal(false)}
            style={{
              padding: "0.6rem 1.2rem",
              fontWeight: 600,
              width: dev.isMobile ? "100%" : "auto",
            }}
          >
            <i className="bi bi-x-circle me-1" />
            Funga
          </button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════
          DELETE MODAL
      ═══════════════════════════════════════════ */}
      <Modal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setRecordToDelete(null);
        }}
        centered
        backdrop="static"
      >
        <div
          style={{
            background: t.card,
            borderRadius: dev.isMobile ? "20px 20px 0 0" : "20px",
            border: `1px solid ${t.bdr}`,
            overflow: "hidden",
          }}
        >
          <div style={{ padding: dev.isMobile ? "1.5rem 1.25rem" : "2rem", textAlign: "center" }}>
            <div
              style={{
                width: dev.isMobile ? 60 : 80,
                height: dev.isMobile ? 60 : 80,
                borderRadius: "50%",
                background: "rgba(255,61,113,0.1)",
                border: `3px solid ${t.dan}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.25rem",
                animation: "pulseDanger 2s infinite",
              }}
            >
              <i
                className="bi bi-exclamation-triangle-fill"
                style={{ fontSize: dev.isMobile ? "1.5rem" : "2.2rem", color: t.dan }}
              />
            </div>

            <h5
              style={{
                color: t.bright,
                fontWeight: 700,
                fontSize: dev.isMobile ? fs.lg : "1.2rem",
                marginBottom: "0.5rem",
              }}
            >
              Gusiba Amakuru
            </h5>
            <p style={{ color: t.muted, fontSize: fs.sm, marginBottom: "0.5rem", lineHeight: 1.5 }}>
              Uremeza ko ushaka gusiba:
            </p>

            {recordToDelete && (
              <div
                style={{
                  background: "rgba(255,61,113,0.08)",
                  border: "1px solid rgba(255,61,113,0.2)",
                  borderRadius: "12px",
                  padding: "0.7rem 1rem",
                  marginBottom: "0.75rem",
                }}
              >
                <strong style={{ color: t.bright, fontSize: fs.md }}>
                  <i className="bi bi-person-fill me-2" style={{ color: t.dan }} />
                  {recordToDelete.amazina_y_umuturage || "Umuturage utazwi"}
                </strong>
              </div>
            )}

            <p style={{ color: t.dim, fontSize: fs.xs, marginBottom: "1.25rem" }}>
              <i className="bi bi-info-circle me-1" />
              Ntigishobora gusubizwaho.
            </p>

            <div
              className="d-flex gap-2 justify-content-center"
              style={{ flexDirection: dev.isMobile ? "column" : "row" }}
            >
              <button
                className="btn-g"
                onClick={() => {
                  setShowDeleteModal(false);
                  setRecordToDelete(null);
                }}
                style={{
                  padding: "0.6rem 1.3rem",
                  fontWeight: 600,
                  width: dev.isMobile ? "100%" : "auto",
                  order: dev.isMobile ? 2 : 1,
                }}
              >
                <i className="bi bi-x-circle me-1" />
                Oya
              </button>
              <button
                className="btn-m"
                onClick={confirmDelete}
                style={{
                  background: `linear-gradient(135deg, ${t.dan}, #b91c1c)`,
                  color: "#ffffff",
                  padding: "0.6rem 1.3rem",
                  fontWeight: 700,
                  boxShadow: `0 4px 14px ${t.danGlow}`,
                  width: dev.isMobile ? "100%" : "auto",
                  order: dev.isMobile ? 1 : 2,
                }}
              >
                <i className="bi bi-trash me-1" />
                Yego, Siba
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default IsukuIsukura;